import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Delete as DeleteIcon,
  Task as TaskIcon,
  Chat as ChatIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import io from 'socket.io-client';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';

// Определяем URL сокета динамически
const getSocketUrl = () => {
  // В продакшне используем тот же хост, что и сайт
  if (window.location.hostname !== 'localhost') {
    return `${window.location.protocol}//${window.location.hostname}`;
  }
  // Для локальной разработки
  return 'http://localhost:5001';
};

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    
    fetchNotifications();
    fetchUnreadCount();

    // Подключаемся к Socket.io для получения уведомлений в реальном времени
    const socketUrl = getSocketUrl();
    console.log('Подключение к Socket.io:', socketUrl);
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;
    
    socket.on('connect', () => {
      if (user) {
        socket.emit('user_connected', {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
        });
      }
    });

    socket.on('new_notification', (notification) => {
      // Проверяем, что уведомление предназначено для текущего пользователя
      // recipient может быть объектом или строкой ID
      const recipientId = notification.recipient?._id || notification.recipient;
      if (recipientId === user?.id) {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        
        // Воспроизводим звук
        playNotificationSound();
        
        // Показываем браузерное уведомление (только если API доступен - НЕ работает на iOS)
        try {
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.message,
              icon: '/logo192.png'
            });
          }
        } catch (e) {
          // iOS Safari не поддерживает Notification API
        }
      }
    });

    // Слушаем события чата для звуковых уведомлений
    socket.on('receive_message', (message) => {
      // Если сообщение от другого пользователя, воспроизводим звук
      if (message.userId !== user?.id) {
        playNotificationSound();
      }
    });

    // Запрашиваем разрешение на показ уведомлений (только если API доступен)
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } catch (e) {
      // iOS Safari не поддерживает Notification API
    }

    // Периодически обновляем счётчик на случай если сокет не работает
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // Каждые 30 секунд

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get('/api/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Ошибка загрузки счетчика:', error);
    }
  };

  const playNotificationSound = () => {
    // Создаем простой звуковой сигнал с помощью Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Частота звука
      oscillator.type = 'sine'; // Тип волны
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // Громкость
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Ошибка воспроизведения звука:', error);
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Отмечаем как прочитанное
      if (!notification.read) {
        await axios.patch(`/api/notifications/${notification._id}/read`);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) =>
          prev.map((n) => (n._id === notification._id ? { ...n, read: true } : n))
        );
      }
      
      // Обработка разных типов уведомлений
      if (notification.type === 'chat_message' && notification.link) {
        // Для чата - отправляем событие для открытия чата
        const chatRoomId = notification.link.split('/').pop();
        window.dispatchEvent(new CustomEvent('openChat', { detail: { chatRoomId } }));
      } else if (notification.link && notification.link !== '#') {
        // Для остальных - переходим по ссылке
        navigate(notification.link);
      }
      
      handleClose();
    } catch (error) {
      console.error('Ошибка обработки уведомления:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch('/api/notifications/mark-all-read');
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Ошибка обновления уведомлений:', error);
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/notifications/${notificationId}`);
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      fetchUnreadCount();
    } catch (error) {
      console.error('Ошибка удаления уведомления:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_created':
      case 'task_updated':
      case 'task_assigned':
        return <TaskIcon />;
      case 'chat_message':
        return <ChatIcon />;
      case 'change_request_created':
        return <AssignmentIcon />;
      case 'change_request_approved':
        return <CheckCircleIcon />;
      case 'change_request_rejected':
        return <CancelIcon />;
      case 'company_pending_approval':
        return <HourglassEmptyIcon />;
      case 'company_approved':
        return <CheckCircleIcon />;
      case 'company_rejected':
        return <CancelIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'task_created':
        return 'primary.main';
      case 'task_updated':
        return 'info.main';
      case 'task_assigned':
        return 'success.main';
      case 'chat_message':
        return 'secondary.main';
      case 'change_request_created':
        return 'warning.main';
      case 'change_request_approved':
        return 'success.main';
      case 'change_request_rejected':
        return 'error.main';
      case 'company_pending_approval':
        return 'warning.main';
      case 'company_approved':
        return 'success.main';
      case 'company_rejected':
        return 'error.main';
      default:
        return 'grey.500';
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Уведомления
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>
              Прочитать все
            </Button>
          )}
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              Нет уведомлений
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 450, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <React.Fragment key={notification._id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getNotificationColor(notification.type) }}>
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <Chip label="Новое" size="small" color="error" sx={{ height: 20 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {format(new Date(notification.createdAt), 'dd MMM, HH:mm', { locale: ru })}
                        </Typography>
                      </>
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteNotification(e, notification._id)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;

